import { Box, Flex, Image, Link, VStack } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import { generateS3DownloadUrl } from '../server/libs/siteSettings';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { SPECIAL_PAGE_GA_CATEGORY } from '../common/constants';

export type MessageAreaSettings = {
  iconImage?: string | null;
  iconImageS3Key?: string | null;
  text: string; // markdown形式の説明文
  caption?: string | string[] | null; // textの下に表示される 配列にするとblock要素になりいい感じで改行される

  // 複数のドクターの画像、名前（肩書き等）を使う場合に使用
  // 値があると画像と名前がセットで複数表示でき上位のiconImage, iconImageS3Keyは無視される
  contributors?: {
    iconImage?: string | null;
    iconImageS3Key?: string | null;
    caption?: string | string[] | null;
  }[];
};

const CaptionContent = ({ caption }: { caption: string | string[] }) => {
  return (
    <>
      {caption
        ? Array.isArray(caption)
          ? caption.map((c, index) => <Box key={index}>{c}</Box>)
          : caption
        : null}
    </>
  );
};

const ImageContent = ({
  iconImage,
  iconImageS3Key,
}: {
  iconImage?: string | null;
  iconImageS3Key?: string | null;
}) =>
  iconImage || iconImageS3Key ? (
    <Image
      src={iconImage ?? generateS3DownloadUrl(iconImageS3Key)}
      alt="メッセージアイコン"
      w={83}
      h={83}
      rounded="full"
    />
  ) : null;

const Contributors = ({
  contributors,
}: {
  contributors: MessageAreaSettings['contributors'];
}) => {
  return (
    <Flex
      justifyContent="flex-start"
      flexDirection="column"
      gap={1}
      flexShrink={0}
    >
      {contributors?.map(({ iconImage, iconImageS3Key, caption }, index) => (
        <Flex
          key={index}
          justifyContent="flex-start"
          alignItems="center"
          flexDirection="column"
          maxW={140}
          gap={1}
        >
          <ImageContent iconImage={iconImage} iconImageS3Key={iconImageS3Key} />
          {caption && (
            <Flex
              fontSize="sm"
              color="textGray"
              whiteSpace="pre-wrap"
              flexWrap="wrap"
              columnGap={1}
              justifyContent="center"
            >
              <CaptionContent caption={caption} />
            </Flex>
          )}
        </Flex>
      ))}
    </Flex>
  );
};

export function MessageArea({
  iconImage,
  iconImageS3Key,
  text,
  textFontSize,
  caption,
  specialArticleId,
  isMobile,
  bgColor,
  contributors,
}: MessageAreaSettings & {
  specialArticleId: string;
  textFontSize?: number | string;
  isMobile: boolean;
  bgColor?: string;
}) {
  const imageContent = (
    <ImageContent iconImage={iconImage} iconImageS3Key={iconImageS3Key} />
  );

  return (
    <Flex
      direction="column"
      backgroundColor={bgColor ?? 'white'}
      py={4}
      px={isMobile ? 2 : 8}
      gap={2}
    >
      <Flex
        gap={isMobile ? 2 : 4}
        w="full"
        justifyContent="flex-start"
        alignItems={!!contributors ? 'flex-start' : undefined}
      >
        {!contributors && imageContent}
        {!!contributors && <Contributors contributors={contributors} />}
        <VStack alignItems="flex-start" gap={1} justifyContent="center">
          <Box
            whiteSpace="pre-wrap"
            fontSize={textFontSize ?? (isMobile ? 'sm' : '15px')}
          >
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <Link
                    {...props}
                    textDecoration="underline"
                    href={props.href}
                    color="textLink"
                    isExternal
                    onClick={(e) => {
                      sendAnalyticsEvent(
                        SPECIAL_PAGE_GA_CATEGORY,
                        'click_link',
                        `${specialArticleId}_${props.href}`,
                      );
                      props.onClick?.(e);
                    }}
                  />
                ),
              }}
            >
              {text}
            </ReactMarkdown>
          </Box>
          {caption && (
            <Flex
              fontSize="sm"
              color="textGray"
              whiteSpace="pre-wrap"
              flexWrap="wrap"
              columnGap={1}
            >
              <CaptionContent caption={caption} />
            </Flex>
          )}
        </VStack>
      </Flex>
    </Flex>
  );
}
